<template>
  <div class="max-w-2xl">
    <div class="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <h2 class="text-base font-semibold text-gray-800 mb-1">Facility Logo</h2>
      <p class="text-sm text-gray-500 mb-5">
        Upload a logo that will appear on patient receipts and printed documents.
        Recommended size: 300 × 100 px. Max file size: 2 MB. Formats: PNG, JPG, SVG.
      </p>

      <!-- Current logo preview -->
      <div class="mb-5">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Current Logo</p>
        <div class="w-48 h-20 border border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden">
          <img
            v-if="currentLogoUrl"
            :src="currentLogoUrl"
            alt="Facility logo"
            class="max-h-full max-w-full object-contain"
          />
          <div v-else class="flex flex-col items-center text-gray-400">
            <svg class="w-8 h-8 mb-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M3 21V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14M3 21h18M9 21V11h6v10"/>
            </svg>
            <span class="text-xs">No logo set</span>
          </div>
        </div>
      </div>

      <!-- Drop zone / file picker -->
      <div
        class="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
        :class="isDragging
          ? 'border-custom-yellow bg-yellow-50'
          : 'border-gray-300 hover:border-custom-yellow hover:bg-gray-50'"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
        @click="$refs.fileInput.click()"
      >
        <svg class="mx-auto w-8 h-8 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
        </svg>
        <p class="text-sm text-gray-600">
          <span class="font-medium text-custom-yellow">Click to upload</span> or drag and drop
        </p>
        <p class="text-xs text-gray-400 mt-1">PNG, JPG, SVG up to 2 MB</p>

        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          class="hidden"
          @change="onFileSelected"
        />
      </div>

      <!-- Preview of newly selected file -->
      <div v-if="previewUrl" class="mt-4">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Preview</p>
        <div class="w-48 h-20 border border-gray-200 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden">
          <img :src="previewUrl" alt="New logo preview" class="max-h-full max-w-full object-contain" />
        </div>
        <p class="text-xs text-gray-400 mt-1">{{ selectedFile?.name }} ({{ formattedFileSize }})</p>
      </div>

      <!-- Validation error -->
      <p v-if="validationError" class="mt-3 text-sm text-red-500 flex items-center gap-1">
        <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        </svg>
        {{ validationError }}
      </p>

      <!-- Actions -->
      <div class="mt-6 flex items-center gap-3">
        <button
          :disabled="!selectedFile || isUploading"
          class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white transition-opacity"
          :class="selectedFile && !isUploading ? 'bg-custom-yellow' : 'bg-gray-300 cursor-not-allowed'"
          @click="uploadLogo"
        >
          <svg v-if="isUploading" class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ isUploading ? "Uploading…" : "Save Logo" }}
        </button>

        <button
          v-if="currentLogoUrl"
          :disabled="isRemoving"
          class="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-red-600 border border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="removeLogo"
        >
          <svg v-if="isRemoving" class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ isRemoving ? "Removing…" : "Remove Logo" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AWS from "aws-sdk";
import apiClient from "@/services/api";

const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES  = ["image/png", "image/jpeg", "image/svg+xml"];

export default {
  name: "FacilityTools",

  data() {
    return {
      currentLogoUrl: null,  // The URL currently saved in the DB for this facility
      selectedFile:   null,  // File object the user picked but hasn't uploaded yet
      previewUrl:     null,  // Local object URL for the preview thumbnail
      validationError: null,
      isDragging:     false,
      isUploading:    false,
      isRemoving:     false,
    };
  },

  computed: {
    formattedFileSize() {
      if (!this.selectedFile) return "";
      const kb = this.selectedFile.size / 1024;
      return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
    },
  },

  mounted() {
    this.fetchCurrentLogo();
  },

  beforeUnmount() {
    // Always revoke object URLs we created to avoid memory leaks
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },

  methods: {
    // ─── Fetch existing logo on component mount ──────────────────────────────

    async fetchCurrentLogo() {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.FacilityId) return;

        const response = await apiClient.fetchFacility(user.FacilityId);
        if (response.success && response.data.logo_url) {
          this.currentLogoUrl = response.data.logo_url;
        }
      } catch (error) {
        this.$toast.error(error.message || 'Failed to fetch current logo. Please check your internet')
        console.error("FacilityTools: failed to fetch current logo:", error);
      }
    },

    // ─── File selection ──────────────────────────────────────────────────────

    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) this.processFile(file);
    },

    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.processFile(file);
    },

    processFile(file) {
      this.validationError = null;

      if (!ALLOWED_TYPES.includes(file.type)) {
        this.validationError = "Invalid file type. Please upload a PNG, JPG, or SVG image.";
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        this.validationError = "File is too large. Maximum allowed size is 2 MB.";
        return;
      }

      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.selectedFile = file;
      this.previewUrl   = URL.createObjectURL(file);
    },

    async uploadLogo() {
      if (!this.selectedFile) return;

      this.isUploading    = true;
      this.validationError = null;

      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.FacilityId) throw new Error("Facility ID not found in session.");

        // Step 1 — Upload the file directly to DigitalOcean Spaces.
        //
        // We use the same env vars and SDK configuration that already works
        // in PatientDocumentUploadForm.vue, so no new infrastructure is needed.
        //
        // The Key uses a dedicated prefix (facility-logos/) plus the facility
        // ID and a timestamp so uploads are namespaced and never collide with
        // patient documents or with each other if the logo is replaced later.
        const s3 = new AWS.S3({
          endpoint:         process.env.VUE_APP_DO_ENDPOINT,
          signatureVersion: "v4",
          accessKeyId:      process.env.VUE_APP_DO_ACCESS_KEY,
          secretAccessKey:  process.env.VUE_APP_DO_SECRET_KEY,
          region:           process.env.VUE_APP_DO_REGION,
        });

        const fileExtension = this.selectedFile.name.split(".").pop();
        const s3Key = `facility-logos/${user.FacilityId}-${Date.now()}.${fileExtension}`;

        const s3Params = {
          Bucket:      "jiji-bucket",
          Key:         s3Key,
          Body:        this.selectedFile,
          ACL:         "public-read",
          ContentType: this.selectedFile.type,
        };

        const result   = await s3.upload(s3Params).promise();
        const logoUrl  = result.Location;

        // Step 2 — Tell Rails to save the public URL to the Facilities table.
        //
        // The file is already safely in the bucket at this point. This second
        // call is lightweight — just a PATCH that writes one string column.
        const response = await apiClient.updateFacilityLogo(user.FacilityId, logoUrl);

        if (response.success) {
          this.currentLogoUrl = logoUrl;
          this.clearSelection();
          this.$toast.success("Logo uploaded successfully.");
        } else {
          this.$toast.error(response.message || "Failed to save logo URL.");
        }
      } catch (error) {
        console.error("FacilityTools: logo upload error:", error);
        this.$toast.error("Unable to upload logo. Please check your internet connection.");
      } finally {
        this.isUploading = false;
      }
    },

    async removeLogo() {
      this.isRemoving = true;
      try {
        const user     = JSON.parse(localStorage.getItem("user"));
        const response = await apiClient.updateFacilityLogo(user.FacilityId, null);

        if (response.success) {
          this.currentLogoUrl = null;
          this.$toast.success("Logo removed.");
        } else {
          this.$toast.error(response.message || "Failed to remove logo.");
        }
      } catch (error) {
        console.error("FacilityTools: logo removal error:", error);
        this.$toast.error("An error occurred while removing the logo.");
      } finally {
        this.isRemoving = false;
      }
    },

    // ─── Helpers ─────────────────────────────────────────────────────────────

    clearSelection() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.selectedFile        = null;
      this.previewUrl          = null;
      this.$refs.fileInput.value = "";
    },
  },
};
</script>

<style scoped></style>